import React, { useState, useEffect } from 'react';
import useConfirmDialog from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../common/ConfirmDialog';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Select,
  MenuItem,
  FormControl
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSubsidiary } from '../../context/SubsidiaryContext';

/**
 * QuickBooks-style connector assignment suggestions component
 * Displays auto-suggested assignments with confidence levels and reasons
 * Follows StoreMappingManager UI pattern with blue card and approve/reject actions
 */
export default function ConnectorSuggestionsCard({ onRefresh }) {
  const { confirmDialogProps, confirm } = useConfirmDialog();
  const { subsidiaries } = useSubsidiary();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [stats, setStats] = useState({});
  const [applying, setApplying] = useState(false);
  const [selectedSubsidiaries, setSelectedSubsidiaries] = useState({}); // Track selected subsidiary per connector

  useEffect(() => {
    loadSuggestions();
  }, []);

  const loadSuggestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await fetch('/api/subsidiaries/connectors/suggest-assignments', {
        headers: { 'x-auth-token': token }
      });

      const data = await response.json();

      if (data.success) {
        const loadedSuggestions = data.suggestions || [];
        setSuggestions(loadedSuggestions);
        setStats(data.stats || {});

        // Initialize selected subsidiaries to suggested values
        const initial = {};
        loadedSuggestions.forEach(s => {
          if (s.suggested_subsidiary_id) {
            initial[s.connector_id] = s.suggested_subsidiary_id;
          }
        });
        setSelectedSubsidiaries(initial);
      } else {
        setError('Failed to load suggestions');
      }
    } catch (err) {
      console.error('Error loading suggestions:', err);
      setError('Failed to load suggestions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const applyAssignment = async (connectorId, subsidiaryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/subsidiaries/${subsidiaryId}/connectors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          connectorId,
          profileIdentifier: '',
          profileName: '',
          isPrimary: true
        })
      });

      const data = await response.json();

      // POST returns 201 with the created row (no success field)
      if (response.ok) {
        // Remove this suggestion from the list
        setSuggestions(prev => prev.filter(s => s.connector_id !== connectorId));

        // Update stats
        setStats(prev => ({
          ...prev,
          total: prev.total - 1,
          with_suggestions: prev.with_suggestions - 1
        }));

        // Notify parent to refresh
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert('Failed to apply assignment: ' + (data.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error applying assignment:', err);
      alert('Failed to apply assignment. Please try again.');
    }
  };

  const rejectSuggestion = (connectorId) => {
    // Just remove from UI without applying
    setSuggestions(prev => prev.filter(s => s.connector_id !== connectorId));
    setStats(prev => ({
      ...prev,
      total: prev.total - 1,
      with_suggestions: prev.with_suggestions - 1
    }));
  };

  const handleSubsidiaryChange = (connectorId, subsidiaryId) => {
    setSelectedSubsidiaries(prev => ({
      ...prev,
      [connectorId]: subsidiaryId
    }));
  };

  const approveAll = async () => {
    const suggestionsWithSelection = suggestions.filter(s => selectedSubsidiaries[s.connector_id]);

    if (!await confirm({
      title: 'Apply Assignments',
      message: `Apply all ${suggestionsWithSelection.length} selected assignments?`,
      confirmText: 'Apply All',
      severity: 'info'
    })) {
      return;
    }

    setApplying(true);

    for (const suggestion of suggestionsWithSelection) {
      await applyAssignment(suggestion.connector_id, selectedSubsidiaries[suggestion.connector_id]);
      // Small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setApplying(false);
    alert('All assignments applied successfully!');
  };

  const getConfidenceColor = (confidence) => {
    switch (confidence) {
      case 'high': return 'success';
      case 'medium': return 'warning';
      case 'low': return 'default';
      default: return 'default';
    }
  };

  const getConfidenceLabel = (confidence) => {
    switch (confidence) {
      case 'high': return 'High Confidence';
      case 'medium': return 'Medium';
      case 'low': return 'Low';
      default: return confidence;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Don't show the card if there are no suggestions
  if (suggestions.length === 0) {
    return null;
  }

  // Filter suggestions that have actual recommended assignments
  const suggestionsWithAssignments = suggestions.filter(s => s.suggested_subsidiary_id);

  return (
    <Card sx={{ mb: 3, bgcolor: '#f5f9ff', borderColor: 'primary.light', borderWidth: 1, borderStyle: 'solid' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoFixHighIcon color="primary" />
            <Typography variant="h6" fontWeight={600} color="primary.main">
              Auto-Suggested Assignments ({suggestionsWithAssignments.length})
            </Typography>
          </Box>

          {suggestionsWithAssignments.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              onClick={approveAll}
              disabled={applying}
              startIcon={applying ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            >
              {applying ? 'Applying...' : 'Approve All'}
            </Button>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Based on currency, geography, and brand context, we suggest the following connector assignments.
          You can change the subsidiary selection before applying.
        </Typography>

        {/* Statistics */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Chip
            label={`${stats.high_confidence || 0} High Confidence`}
            color="success"
            size="small"
          />
          <Chip
            label={`${stats.medium_confidence || 0} Medium Confidence`}
            color="warning"
            size="small"
          />
          <Chip
            label={`${stats.low_confidence || 0} Need Review`}
            color="default"
            size="small"
          />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Connector</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Platform</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Brand</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Assign to Subsidiary</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Confidence</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Match Details</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {suggestionsWithAssignments.map((suggestion) => (
                <TableRow key={suggestion.connector_id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {suggestion.connector_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={suggestion.platform_name} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{suggestion.brand_name || '-'}</TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth sx={{ minWidth: 180 }}>
                      <Select
                        value={selectedSubsidiaries[suggestion.connector_id] || ''}
                        onChange={(e) => handleSubsidiaryChange(suggestion.connector_id, e.target.value)}
                        displayEmpty
                      >
                        {subsidiaries.filter(s => !s.is_default).map((sub) => (
                          <MenuItem key={sub.id} value={sub.id}>
                            {sub.name}
                            {sub.id === suggestion.suggested_subsidiary_id && (
                              <Chip label="Suggested" size="small" color="primary" sx={{ ml: 1, height: 18 }} />
                            )}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getConfidenceLabel(suggestion.confidence)}
                      color={getConfidenceColor(suggestion.confidence)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ maxWidth: 200 }} noWrap>
                        {suggestion.reasons[0] || '-'}
                      </Typography>
                      {suggestion.reasons.length > 1 && (
                        <Tooltip title={suggestion.reasons.join(', ')}>
                          <InfoOutlinedIcon fontSize="small" color="action" />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => applyAssignment(suggestion.connector_id, selectedSubsidiaries[suggestion.connector_id])}
                      title="Apply Assignment"
                      disabled={!selectedSubsidiaries[suggestion.connector_id]}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => rejectSuggestion(suggestion.connector_id)}
                      title="Dismiss Suggestion"
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              {suggestionsWithAssignments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No high-confidence suggestions found. Manual review recommended.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <ConfirmDialog {...confirmDialogProps} />
    </Card>
  );
}
